// The owner-facing side of a Neo alarm PROPOSAL (ADR 0161, phase 4).
//
// Neo never writes an alarm. It drafts one, the cloud dry-runs the draft through
// the same validator the real create runs, and the accepted draft is reported to
// this client as a structured record — on the blocking body as `proposals`, and
// on the stream as an `alarm_proposal` SSE event. The owner's CLICK on the
// confirmation card is what creates the alarm, via the ordinary owner route
// `POST /v1/boats/:boatId/alerts`.
//
// Everything here is pure so it can be tested without a DOM (cloud/web's vitest
// config runs `src/**/*.test.ts` under Node, no jsdom). The card component
// (components/NeoAlarmProposalCard.tsx) holds only React state and the fetch.
//
// The record shape mirrors `cloud/src/core/neo/alarm-tools.ts`
// (`NeoAlarmProposal` / `NeoAlarmProposalDisplay`). It is decoded DEFENSIVELY:
// the payload arrives over SSE from a route whose contents are ultimately shaped
// by model arguments, so a malformed entry is dropped rather than rendered.

import { alarmSeverityLabel, ALARM_SEVERITIES, type AlarmSeverity } from './alarmDisplay';
import { mapCreateError } from './customAlarmErrors';

/** The card content the server built — see `NeoAlarmProposalDisplay` (cloud). */
export interface NeoAlarmProposalDisplay {
  readonly title: string;
  readonly severity: AlarmSeverity;
  readonly kind: 'recipe' | 'custom';
  /** The recipe's catalog title (recipe proposals only). */
  readonly recipeTitle?: string;
  /** The pinned signal + trigger class (custom proposals only). */
  readonly custom?: { readonly path: string; readonly triggerClass: string };
  readonly enabledModes: readonly string[];
  /**
   * Human threshold lines, already carrying units where the catalog knows them —
   * and already in the OWNER's unit ("Wait before setting off: 60 seconds", not
   * "…: 60000 ms"). The server converts them when it builds the card
   * (`thresholdLines` in cloud/src/core/neo/alarm-tools.ts) because only it holds
   * the recipe metadata that says which params convert. Render them verbatim:
   * converting again here would divide a number that has already been divided.
   */
  readonly thresholds: readonly string[];
  /** One-line rendering of the qualifier conditions, when present. */
  readonly conditions?: string;
  /** The concrete-instance pin's path, when present (recipe proposals only). */
  readonly bindingPath?: string;
}

/** One DRAFT alarm awaiting the owner's click. `body` is POSTed verbatim. */
export interface NeoAlarmProposal {
  readonly boatId: string;
  /**
   * A client-minted stable identity, created once at decode. It is BOTH the
   * card's React key and the create's Idempotency-Key: positional keys let a
   * sibling's discard remount a busy card and misdirect its completion, and a
   * fresh key per click let a double confirm create a custom alarm twice —
   * one identity, held for the proposal's whole life, closes both.
   */
  readonly clientId: string;
  /** The create body, opaque here — it is relayed to the API unchanged. */
  readonly body: Record<string, unknown>;
  readonly display: NeoAlarmProposalDisplay;
}

/** `POST /v1/boats/:boatId/alerts` → 201 (contract §5.7). */
export interface AlarmCreateResponse {
  readonly id: string;
  readonly compileStatus: string | null;
  readonly rulesetVersion: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOf(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function stringArrayOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function severityOf(value: unknown): AlarmSeverity | undefined {
  return typeof value === 'string' && (ALARM_SEVERITIES as readonly string[]).includes(value)
    ? (value as AlarmSeverity)
    : undefined;
}

function displayOf(raw: unknown): NeoAlarmProposalDisplay | null {
  if (!isRecord(raw)) return null;
  const title = stringOf(raw.title);
  const severity = severityOf(raw.severity);
  const kind = raw.kind === 'recipe' || raw.kind === 'custom' ? raw.kind : undefined;
  if (title === undefined || severity === undefined || kind === undefined) return null;

  let custom: { path: string; triggerClass: string } | undefined;
  if (isRecord(raw.custom)) {
    const path = stringOf(raw.custom.path);
    const triggerClass = stringOf(raw.custom.triggerClass);
    if (path !== undefined && triggerClass !== undefined) custom = { path, triggerClass };
  }
  // A custom proposal with no readable pin cannot be described honestly — the
  // card would claim to watch a signal it cannot name. Drop it.
  if (kind === 'custom' && custom === undefined) return null;

  return {
    title,
    severity,
    kind,
    ...(stringOf(raw.recipeTitle) !== undefined ? { recipeTitle: raw.recipeTitle as string } : {}),
    ...(custom !== undefined ? { custom } : {}),
    enabledModes: stringArrayOf(raw.enabledModes),
    thresholds: stringArrayOf(raw.thresholds),
    ...(stringOf(raw.conditions) !== undefined ? { conditions: raw.conditions as string } : {}),
    ...(stringOf(raw.bindingPath) !== undefined ? { bindingPath: raw.bindingPath as string } : {}),
  };
}

/**
 * Decode the `{ proposals }` payload of an `alarm_proposal` SSE event (or the
 * blocking route's `proposals` body field) into renderable records. Anything
 * that does not decode is dropped: a card that cannot state what it would create
 * must never be shown, because the owner's click is the only consent there is.
 *
 * `expectedBoatId` is the page-scoped surfaces' defense-in-depth: a proposal
 * stamped with a DIFFERENT boat is dropped rather than rendered, because the
 * card names no boat and its confirm posts to the proposal's own id — on a
 * boat page, a card that would write elsewhere must never appear. The server
 * refuses the mismatch first (`resolveScopedToolBoatId`); this catches
 * anything that slips a layer. Omit it on the general chat surface, which is
 * legitimately multi-boat.
 */
export function parseAlarmProposals(payload: unknown, expectedBoatId?: string): NeoAlarmProposal[] {
  const list = isRecord(payload) ? payload.proposals : payload;
  if (!Array.isArray(list)) return [];
  const out: NeoAlarmProposal[] = [];
  for (const raw of list) {
    if (!isRecord(raw)) continue;
    const boatId = stringOf(raw.boatId);
    if (boatId === undefined || !isRecord(raw.body)) continue;
    if (expectedBoatId !== undefined && boatId !== expectedBoatId) continue;
    const display = displayOf(raw.display);
    if (display === null) continue;
    out.push({ boatId, clientId: crypto.randomUUID(), body: raw.body, display });
  }
  return out;
}

/** The severity chip's label. Same vocabulary as the Alarms page. */
export function proposalSeverityLabel(severity: AlarmSeverity): string {
  return alarmSeverityLabel(severity);
}

/** Plain English for a custom alarm's trigger class. */
export function triggerClassPhrase(triggerClass: string): string {
  switch (triggerClass) {
    case 'High':
      return 'rises above the set point';
    case 'Low':
      return 'falls below the set point';
    case 'On':
      return 'turns on';
    case 'Off':
      return 'turns off';
    case 'DataUnavailable':
      return 'stops reporting';
    default:
      return triggerClass;
  }
}

/**
 * The "what it watches" line. A recipe names its catalog entry; a custom alarm
 * names the signal it is pinned to and what makes it fire. Never invents a
 * description it does not have.
 */
export function proposalWatchLine(display: NeoAlarmProposalDisplay): string {
  if (display.kind === 'recipe') {
    return display.recipeTitle ?? display.title;
  }
  const custom = display.custom;
  if (custom === undefined) return display.title;
  return `${custom.path} — fires when it ${triggerClassPhrase(custom.triggerClass)}`;
}

/**
 * The honest outcome line after a successful create. `compileStatus: 'pending'`
 * is a SUCCESS (the alarm is saved) but it is not yet bound to a signal, so it
 * must never be narrated as "watching" — same rule the Alarms page's saved
 * banner follows (`AlarmDetail.savedBanner`).
 */
export function proposalCreatedMessage(compileStatus: string | null): string {
  if (compileStatus === 'pending') {
    return 'Created — saved, but it isn’t watching a signal yet.';
  }
  if (compileStatus === 'disabled') {
    return 'Created — but it is disabled, so it isn’t evaluating.';
  }
  return 'Created — your boat is watching it now.';
}

/** Whether a create outcome deserves the attention tone rather than the plain one. */
export function proposalCreatedNeedsAttention(compileStatus: string | null): boolean {
  return compileStatus === 'pending' || compileStatus === 'disabled';
}

/**
 * A failed create, in plain English. Reuses the builder's own translation
 * (`mapCreateError`) so a proposal card and the custom-alarm form describe the
 * same rejection the same way; the card has no fields to place an error beside,
 * so only the message is used.
 */
export function proposalCreateErrorMessage(err: unknown): string {
  return mapCreateError(err).message;
}
